// src/utils/emotionalCorrelations.ts
// Sanctuary-tier emotional & mental symptom correlation utilities

import { supabase } from '@/integrations/supabase/client';
import { subDays, format } from 'date-fns';

export interface EmotionalCorrelationResult {
  correlation_type: string;
  metric_a: string;
  metric_b: string;
  avg_metric_a_low: number;
  avg_metric_a_high: number;
  avg_metric_b_low: number;
  avg_metric_b_high: number;
  interpretation: string;
  sample_size: number;
}

interface EmotionalLogRow {
  date: string;
  anxiety_level: number | null;
  depression_level: number | null;
  rumination_minutes: number | null;
  intrusive_thoughts: number | null;
  panic_episodes: number | null;
  compulsions: number | null;
  binge_urge: number | null;
  trauma_activation: number | null;
}

interface SleepLogRow {
  log_date: string;
  hours_slept: number | null;
  quality_rating: number | null;
}

interface WellnessActivityRow {
  activity_type: string;
  duration_minutes: number | null;
  started_at: string;
}

interface FoodLogRow {
  date: string;
  processing_level: string | null;
  mood_impact: number | null;
}

// Helper to calculate median
function median(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

// Helper to calculate average
function average(values: number[]): number {
  if (values.length === 0) return 0;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

/**
 * Correlate sleep quality with anxiety levels
 */
export async function getSleepAnxietyCorrelation(
  userId: string,
  days: number = 60
): Promise<EmotionalCorrelationResult | null> {
  const startDate = format(subDays(new Date(), days), 'yyyy-MM-dd');
  
  // Fetch emotional logs
  const { data: emotionalLogs, error: emotionalError } = await supabase
    .from('emotional_symptom_logs')
    .select('date, anxiety_level')
    .eq('user_id', userId)
    .gte('date', startDate)
    .order('date', { ascending: true });
    
  if (emotionalError || !emotionalLogs?.length) return null;
  
  // Fetch sleep logs - use correct column names
  const { data: sleepLogs, error: sleepError } = await supabase
    .from('sleep_tracking_logs')
    .select('log_date, time_asleep_minutes, quality_rating')
    .eq('user_id', userId)
    .gte('log_date', startDate);
    
  if (sleepError || !sleepLogs?.length) return null;
  
  // Match by date
  const sleepByDate = new Map(sleepLogs.map(s => [s.log_date, s]));
  const matchedDays: { sleep: number; anxiety: number }[] = [];
  
  emotionalLogs.forEach(e => {
    const sleep = sleepByDate.get(e.date);
    if (sleep && e.anxiety_level !== null && sleep.quality_rating !== null) {
      matchedDays.push({
        sleep: sleep.quality_rating,
        anxiety: e.anxiety_level
      });
    }
  });
  
  if (matchedDays.length < 7) return null;
  
  // Split by median sleep quality
  const sleepMedian = median(matchedDays.map(d => d.sleep));
  const lowSleep = matchedDays.filter(d => d.sleep < sleepMedian);
  const highSleep = matchedDays.filter(d => d.sleep >= sleepMedian);
  
  if (lowSleep.length < 3 || highSleep.length < 3) return null;
  
  const avgAnxietyLowSleep = average(lowSleep.map(d => d.anxiety));
  const avgAnxietyHighSleep = average(highSleep.map(d => d.anxiety));
  const diff = avgAnxietyLowSleep - avgAnxietyHighSleep;
  
  let interpretation = '';
  if (diff > 1.5) {
    interpretation = `On nights when you sleep poorly, your anxiety tends to be noticeably higher (avg ${avgAnxietyLowSleep.toFixed(1)} vs ${avgAnxietyHighSleep.toFixed(1)}). Prioritising sleep quality may help regulate your nervous system.`;
  } else if (diff > 0.5) {
    interpretation = `There's a gentle pattern: poorer sleep is linked to slightly higher anxiety. Your system seems sensitive to rest quality.`;
  } else {
    interpretation = `Your anxiety levels appear relatively stable regardless of sleep quality, though good sleep always supports mental health.`;
  }
  
  return {
    correlation_type: 'sleep_anxiety',
    metric_a: 'Sleep Quality',
    metric_b: 'Anxiety Level',
    avg_metric_a_low: average(lowSleep.map(d => d.sleep)),
    avg_metric_a_high: average(highSleep.map(d => d.sleep)),
    avg_metric_b_low: avgAnxietyLowSleep,
    avg_metric_b_high: avgAnxietyHighSleep,
    interpretation,
    sample_size: matchedDays.length
  };
}

/**
 * Correlate EFT/tapping practice with panic episodes
 */
export async function getEFTPanicCorrelation(
  userId: string,
  days: number = 60
): Promise<EmotionalCorrelationResult | null> {
  const startDate = format(subDays(new Date(), days), 'yyyy-MM-dd');
  
  // Fetch emotional logs
  const { data: emotionalLogs, error: emotionalError } = await supabase
    .from('emotional_symptom_logs')
    .select('date, panic_episodes')
    .eq('user_id', userId)
    .gte('date', startDate);
    
  if (emotionalError || !emotionalLogs?.length) return null;
  
  // Fetch EFT activities
  const { data: activities, error: activityError } = await supabase
    .from('wellness_activities')
    .select('started_at, duration_minutes, activity_type')
    .eq('user_id', userId)
    .ilike('activity_type', '%eft%')
    .gte('started_at', `${startDate}T00:00:00`);
    
  if (activityError) return null;
  
  // Aggregate EFT minutes by date
  const eftByDate = new Map<string, number>();
  activities?.forEach(a => {
    const date = a.started_at.split('T')[0];
    eftByDate.set(date, (eftByDate.get(date) || 0) + (a.duration_minutes || 0));
  });
  
  const matchedDays: { eftMinutes: number; panic: number }[] = [];
  emotionalLogs.forEach(e => {
    if (e.panic_episodes !== null) {
      matchedDays.push({
        eftMinutes: eftByDate.get(e.date) || 0,
        panic: e.panic_episodes
      });
    }
  });
  
  if (matchedDays.length < 7) return null;
  
  // Split by EFT practice (any vs none)
  const withEFT = matchedDays.filter(d => d.eftMinutes > 0);
  const withoutEFT = matchedDays.filter(d => d.eftMinutes === 0);
  
  if (withEFT.length < 3 || withoutEFT.length < 3) return null;
  
  const avgPanicWithEFT = average(withEFT.map(d => d.panic));
  const avgPanicWithoutEFT = average(withoutEFT.map(d => d.panic));
  const diff = avgPanicWithoutEFT - avgPanicWithEFT;
  
  let interpretation = '';
  if (diff > 0.5) {
    interpretation = `Days when you practice EFT/tapping show fewer panic episodes (avg ${avgPanicWithEFT.toFixed(1)} vs ${avgPanicWithoutEFT.toFixed(1)}). Your nervous system responds well to this somatic practice.`;
  } else if (diff > 0) {
    interpretation = `There's a slight reduction in panic episodes on days you practice EFT. Consistent practice may deepen this effect.`;
  } else {
    interpretation = `EFT practice doesn't show a clear link to panic episodes yet, but somatic work often has cumulative benefits.`;
  }
  
  return {
    correlation_type: 'eft_panic',
    metric_a: 'EFT Practice',
    metric_b: 'Panic Episodes',
    avg_metric_a_low: 0,
    avg_metric_a_high: average(withEFT.map(d => d.eftMinutes)),
    avg_metric_b_low: avgPanicWithoutEFT,
    avg_metric_b_high: avgPanicWithEFT,
    interpretation,
    sample_size: matchedDays.length
  };
}

/**
 * Correlate ultra-processed food with mood/irritability
 */
export async function getUPFMoodCorrelation(
  userId: string,
  days: number = 60
): Promise<EmotionalCorrelationResult | null> {
  const startDate = format(subDays(new Date(), days), 'yyyy-MM-dd');
  
  // Fetch emotional logs
  const { data: emotionalLogs, error: emotionalError } = await supabase
    .from('emotional_symptom_logs')
    .select('date, depression_level')
    .eq('user_id', userId)
    .gte('date', startDate);
    
  if (emotionalError || !emotionalLogs?.length) return null;
  
  // Fetch food logs - count ultra-processed items per day
  const { data: foodLogs, error: foodError } = await supabase
    .from('food_logs')
    .select('date, processing_level')
    .eq('user_id', userId)
    .gte('date', startDate);
    
  if (foodError) return null;
  
  // Count UPF items by date
  const upfByDate = new Map<string, number>();
  foodLogs?.forEach(f => {
    if (f.processing_level === 'ultra_processed') {
      upfByDate.set(f.date, (upfByDate.get(f.date) || 0) + 1);
    }
  });
  
  const matchedDays: { upfCount: number; depression: number }[] = [];
  emotionalLogs.forEach(e => {
    if (e.depression_level !== null) {
      matchedDays.push({
        upfCount: upfByDate.get(e.date) || 0,
        depression: e.depression_level
      });
    }
  });
  
  if (matchedDays.length < 7) return null;
  
  // Split by UPF consumption
  const highUPF = matchedDays.filter(d => d.upfCount >= 2);
  const lowUPF = matchedDays.filter(d => d.upfCount < 2);
  
  if (highUPF.length < 3 || lowUPF.length < 3) return null;
  
  const avgDepressionHighUPF = average(highUPF.map(d => d.depression));
  const avgDepressionLowUPF = average(lowUPF.map(d => d.depression));
  const diff = avgDepressionHighUPF - avgDepressionLowUPF;
  
  let interpretation = '';
  if (diff > 1) {
    interpretation = `Days with more ultra-processed foods correlate with higher depression scores (avg ${avgDepressionHighUPF.toFixed(1)} vs ${avgDepressionLowUPF.toFixed(1)}). Your mood appears sensitive to food quality.`;
  } else if (diff > 0.3) {
    interpretation = `There's a gentle link between processed food intake and lower mood. Whole foods may support emotional regulation.`;
  } else {
    interpretation = `Food processing level doesn't show a strong connection to your mood scores in this period.`;
  }
  
  return {
    correlation_type: 'upf_mood',
    metric_a: 'Ultra-Processed Foods',
    metric_b: 'Depression Level',
    avg_metric_a_low: average(lowUPF.map(d => d.upfCount)),
    avg_metric_a_high: average(highUPF.map(d => d.upfCount)),
    avg_metric_b_low: avgDepressionLowUPF,
    avg_metric_b_high: avgDepressionHighUPF,
    interpretation,
    sample_size: matchedDays.length
  };
}

/**
 * Correlate meditation with trauma activation
 */
export async function getMeditationTraumaCorrelation(
  userId: string,
  days: number = 60
): Promise<EmotionalCorrelationResult | null> {
  const startDate = format(subDays(new Date(), days), 'yyyy-MM-dd');
  
  // Fetch emotional logs
  const { data: emotionalLogs, error: emotionalError } = await supabase
    .from('emotional_symptom_logs')
    .select('date, trauma_activation')
    .eq('user_id', userId)
    .gte('date', startDate);
    
  if (emotionalError || !emotionalLogs?.length) return null;
  
  // Fetch meditation activities
  const { data: activities, error: activityError } = await supabase
    .from('wellness_activities')
    .select('started_at, duration_minutes, activity_type')
    .eq('user_id', userId)
    .ilike('activity_type', '%meditation%')
    .gte('started_at', `${startDate}T00:00:00`);
    
  if (activityError) return null;
  
  // Aggregate meditation minutes by date
  const meditationByDate = new Map<string, number>();
  activities?.forEach(a => {
    const date = a.started_at.split('T')[0];
    meditationByDate.set(date, (meditationByDate.get(date) || 0) + (a.duration_minutes || 0));
  });
  
  const matchedDays: { meditationMinutes: number; trauma: number }[] = [];
  emotionalLogs.forEach(e => {
    if (e.trauma_activation !== null) {
      matchedDays.push({
        meditationMinutes: meditationByDate.get(e.date) || 0,
        trauma: e.trauma_activation
      });
    }
  });
  
  if (matchedDays.length < 7) return null;
  
  // Split by meditation practice
  const withMeditation = matchedDays.filter(d => d.meditationMinutes > 0);
  const withoutMeditation = matchedDays.filter(d => d.meditationMinutes === 0);
  
  if (withMeditation.length < 3 || withoutMeditation.length < 3) return null;
  
  const avgTraumaWithMeditation = average(withMeditation.map(d => d.trauma));
  const avgTraumaWithoutMeditation = average(withoutMeditation.map(d => d.trauma));
  const diff = avgTraumaWithoutMeditation - avgTraumaWithMeditation;
  
  let interpretation = '';
  if (diff > 1) {
    interpretation = `Meditation practice correlates with lower trauma activation scores (avg ${avgTraumaWithMeditation.toFixed(1)} vs ${avgTraumaWithoutMeditation.toFixed(1)}). This practice appears to support your nervous system regulation.`;
  } else if (diff > 0.3) {
    interpretation = `There's a gentle link between meditation and reduced trauma activation. Consistent practice may strengthen this effect.`;
  } else {
    interpretation = `Meditation doesn't show a clear link to trauma activation levels yet. Consider trauma-informed practices for deeper support.`;
  }
  
  return {
    correlation_type: 'meditation_trauma',
    metric_a: 'Meditation Practice',
    metric_b: 'Trauma Activation',
    avg_metric_a_low: 0,
    avg_metric_a_high: average(withMeditation.map(d => d.meditationMinutes)),
    avg_metric_b_low: avgTraumaWithoutMeditation,
    avg_metric_b_high: avgTraumaWithMeditation,
    interpretation,
    sample_size: matchedDays.length
  };
}

/**
 * Correlate rumination with overall mood changes
 */
export async function getRuminationMoodCorrelation(
  userId: string,
  days: number = 60
): Promise<EmotionalCorrelationResult | null> {
  const startDate = format(subDays(new Date(), days), 'yyyy-MM-dd');
  
  // Fetch emotional logs with rumination and depression
  const { data: emotionalLogs, error: emotionalError } = await supabase
    .from('emotional_symptom_logs')
    .select('date, rumination_minutes, depression_level, anxiety_level')
    .eq('user_id', userId)
    .gte('date', startDate);
    
  if (emotionalError || !emotionalLogs?.length) return null;
  
  const matchedDays: { rumination: number; mood: number }[] = [];
  emotionalLogs.forEach(e => {
    if (e.rumination_minutes !== null && (e.depression_level !== null || e.anxiety_level !== null)) {
      const avgMood = ((e.depression_level || 0) + (e.anxiety_level || 0)) / 2;
      matchedDays.push({
        rumination: e.rumination_minutes,
        mood: avgMood
      });
    }
  });
  
  if (matchedDays.length < 7) return null;
  
  // Split by median rumination
  const ruminationMedian = median(matchedDays.map(d => d.rumination));
  const highRumination = matchedDays.filter(d => d.rumination > ruminationMedian);
  const lowRumination = matchedDays.filter(d => d.rumination <= ruminationMedian);
  
  if (highRumination.length < 3 || lowRumination.length < 3) return null;
  
  const avgMoodHighRumination = average(highRumination.map(d => d.mood));
  const avgMoodLowRumination = average(lowRumination.map(d => d.mood));
  const diff = avgMoodHighRumination - avgMoodLowRumination;
  
  let interpretation = '';
  if (diff > 1) {
    interpretation = `Higher rumination time strongly correlates with worse mood scores (avg ${avgMoodHighRumination.toFixed(1)} vs ${avgMoodLowRumination.toFixed(1)}). Interrupt patterns of overthinking when you notice them.`;
  } else if (diff > 0.3) {
    interpretation = `More rumination tends to coincide with lower mood. Grounding practices may help break rumination cycles.`;
  } else {
    interpretation = `Rumination time doesn't show a strong link to mood in your data yet.`;
  }
  
  return {
    correlation_type: 'rumination_mood',
    metric_a: 'Rumination Minutes',
    metric_b: 'Mood Score',
    avg_metric_a_low: average(lowRumination.map(d => d.rumination)),
    avg_metric_a_high: average(highRumination.map(d => d.rumination)),
    avg_metric_b_low: avgMoodLowRumination,
    avg_metric_b_high: avgMoodHighRumination,
    interpretation,
    sample_size: matchedDays.length
  };
}

// =============================================================================
// EXTENDED EMOTIONAL/MENTAL CORRELATIONS
// These helpers correlate wellness activities with the extended emotional & mental
// pattern metrics. All interpretations are non-diagnostic and reflection-focused.
// =============================================================================

/**
 * Correlate sleep quality with brain fog score
 */
export async function getCorrelationSleepVsBrainFog(
  userId: string,
  days: number = 60
): Promise<EmotionalCorrelationResult | null> {
  const startDate = format(subDays(new Date(), days), 'yyyy-MM-dd');
  
  // Fetch emotional logs with brain fog
  const { data: emotionalLogs, error: emotionalError } = await supabase
    .from('emotional_symptom_logs')
    .select('date, brain_fog_score')
    .eq('user_id', userId)
    .gte('date', startDate);
    
  if (emotionalError || !emotionalLogs?.length) return null;
  
  // Fetch sleep logs
  const { data: sleepLogs, error: sleepError } = await supabase
    .from('sleep_tracking_logs')
    .select('log_date, time_asleep_minutes, quality_rating')
    .eq('user_id', userId)
    .gte('log_date', startDate);
    
  if (sleepError || !sleepLogs?.length) return null;
  
  // Match by date
  const sleepByDate = new Map(sleepLogs.map(s => [s.log_date, s]));
  const matchedDays: { sleepQuality: number; brainFog: number }[] = [];
  
  emotionalLogs.forEach(e => {
    const sleep = sleepByDate.get(e.date);
    if (sleep && e.brain_fog_score !== null && sleep.quality_rating !== null) {
      matchedDays.push({
        sleepQuality: sleep.quality_rating,
        brainFog: Number(e.brain_fog_score)
      });
    }
  });
  
  if (matchedDays.length < 7) return null;
  
  // Split by median sleep quality
  const sleepMedian = median(matchedDays.map(d => d.sleepQuality));
  const lowSleep = matchedDays.filter(d => d.sleepQuality < sleepMedian);
  const highSleep = matchedDays.filter(d => d.sleepQuality >= sleepMedian);
  
  if (lowSleep.length < 3 || highSleep.length < 3) return null;
  
  const avgBrainFogLowSleep = average(lowSleep.map(d => d.brainFog));
  const avgBrainFogHighSleep = average(highSleep.map(d => d.brainFog));
  const diff = avgBrainFogLowSleep - avgBrainFogHighSleep;
  
  let interpretation = '';
  if (diff > 1.5) {
    interpretation = `On nights when you sleep poorly, you tend to experience more brain fog the next day (avg ${avgBrainFogLowSleep.toFixed(1)} vs ${avgBrainFogHighSleep.toFixed(1)}). Quality rest appears to support your mental clarity.`;
  } else if (diff > 0.5) {
    interpretation = `There's a gentle pattern linking poorer sleep to slightly more brain fog. Your cognitive clarity may be sensitive to rest quality.`;
  } else {
    interpretation = `Your brain fog levels appear relatively stable regardless of sleep quality, though good sleep always supports cognitive function.`;
  }
  
  return {
    correlation_type: 'sleep_brain_fog',
    metric_a: 'Sleep Quality',
    metric_b: 'Brain Fog',
    avg_metric_a_low: average(lowSleep.map(d => d.sleepQuality)),
    avg_metric_a_high: average(highSleep.map(d => d.sleepQuality)),
    avg_metric_b_low: avgBrainFogLowSleep,
    avg_metric_b_high: avgBrainFogHighSleep,
    interpretation,
    sample_size: matchedDays.length
  };
}

/**
 * Correlate EFT/tapping practice with anxiety and panic intensity
 */
export async function getCorrelationEFTVsAnxietyAndPanic(
  userId: string,
  days: number = 60
): Promise<EmotionalCorrelationResult | null> {
  const startDate = format(subDays(new Date(), days), 'yyyy-MM-dd');
  
  // Fetch emotional logs with anxiety and panic intensity
  const { data: emotionalLogs, error: emotionalError } = await supabase
    .from('emotional_symptom_logs')
    .select('date, anxiety_level, panic_intensity_score')
    .eq('user_id', userId)
    .gte('date', startDate);
    
  if (emotionalError || !emotionalLogs?.length) return null;
  
  // Fetch EFT activities
  const { data: activities, error: activityError } = await supabase
    .from('wellness_activities')
    .select('started_at, duration_minutes, activity_type')
    .eq('user_id', userId)
    .ilike('activity_type', '%eft%')
    .gte('started_at', `${startDate}T00:00:00`);
    
  if (activityError) return null;
  
  // Aggregate EFT minutes by date
  const eftByDate = new Map<string, number>();
  activities?.forEach(a => {
    const date = a.started_at.split('T')[0];
    eftByDate.set(date, (eftByDate.get(date) || 0) + (a.duration_minutes || 0));
  });
  
  const matchedDays: { eftMinutes: number; combinedScore: number }[] = [];
  emotionalLogs.forEach(e => {
    const anxiety = e.anxiety_level !== null ? e.anxiety_level : null;
    const panic = e.panic_intensity_score !== null ? Number(e.panic_intensity_score) : null;
    
    if (anxiety !== null || panic !== null) {
      const scores = [anxiety, panic].filter((s): s is number => s !== null);
      if (scores.length > 0) {
        matchedDays.push({
          eftMinutes: eftByDate.get(e.date) || 0,
          combinedScore: average(scores)
        });
      }
    }
  });
  
  if (matchedDays.length < 7) return null;
  
  // Split by EFT practice (any vs none)
  const withEFT = matchedDays.filter(d => d.eftMinutes > 0);
  const withoutEFT = matchedDays.filter(d => d.eftMinutes === 0);
  
  if (withEFT.length < 3 || withoutEFT.length < 3) return null;
  
  const avgScoreWithEFT = average(withEFT.map(d => d.combinedScore));
  const avgScoreWithoutEFT = average(withoutEFT.map(d => d.combinedScore));
  const diff = avgScoreWithoutEFT - avgScoreWithEFT;
  
  let interpretation = '';
  if (diff > 1) {
    interpretation = `On days when you practice EFT/tapping, your anxiety and panic intensity tend to be lower (avg ${avgScoreWithEFT.toFixed(1)} vs ${avgScoreWithoutEFT.toFixed(1)}). This somatic practice appears to support your nervous system.`;
  } else if (diff > 0.3) {
    interpretation = `There's a gentle pattern linking EFT practice to slightly lower anxiety and panic. Consistent practice may strengthen this effect.`;
  } else {
    interpretation = `EFT practice doesn't show a clear pattern with anxiety and panic yet, though somatic work often has cumulative benefits over time.`;
  }
  
  return {
    correlation_type: 'eft_anxiety_panic',
    metric_a: 'EFT Practice',
    metric_b: 'Anxiety & Panic Intensity',
    avg_metric_a_low: 0,
    avg_metric_a_high: average(withEFT.map(d => d.eftMinutes)),
    avg_metric_b_low: avgScoreWithoutEFT,
    avg_metric_b_high: avgScoreWithEFT,
    interpretation,
    sample_size: matchedDays.length
  };
}

/**
 * Correlate meditation with rumination and intrusive thoughts
 */
export async function getCorrelationMeditationVsRuminationAndIntrusions(
  userId: string,
  days: number = 60
): Promise<EmotionalCorrelationResult | null> {
  const startDate = format(subDays(new Date(), days), 'yyyy-MM-dd');
  
  // Fetch emotional logs with rumination and intrusive thoughts
  const { data: emotionalLogs, error: emotionalError } = await supabase
    .from('emotional_symptom_logs')
    .select('date, rumination_minutes, intrusive_thoughts_score, thought_looping_score')
    .eq('user_id', userId)
    .gte('date', startDate);
    
  if (emotionalError || !emotionalLogs?.length) return null;
  
  // Fetch meditation activities
  const { data: activities, error: activityError } = await supabase
    .from('wellness_activities')
    .select('started_at, duration_minutes, activity_type')
    .eq('user_id', userId)
    .ilike('activity_type', '%meditation%')
    .gte('started_at', `${startDate}T00:00:00`);
    
  if (activityError) return null;
  
  // Aggregate meditation minutes by date
  const meditationByDate = new Map<string, number>();
  activities?.forEach(a => {
    const date = a.started_at.split('T')[0];
    meditationByDate.set(date, (meditationByDate.get(date) || 0) + (a.duration_minutes || 0));
  });
  
  const matchedDays: { meditationMinutes: number; thoughtScore: number }[] = [];
  emotionalLogs.forEach(e => {
    // Normalize rumination minutes to 0-10 scale (assuming 60+ min is max)
    const ruminationScore = e.rumination_minutes !== null ? Math.min(Number(e.rumination_minutes) / 6, 10) : null;
    const intrusiveScore = e.intrusive_thoughts_score !== null ? Number(e.intrusive_thoughts_score) : null;
    const loopingScore = e.thought_looping_score !== null ? Number(e.thought_looping_score) : null;
    
    const scores = [ruminationScore, intrusiveScore, loopingScore].filter((s): s is number => s !== null);
    if (scores.length > 0) {
      matchedDays.push({
        meditationMinutes: meditationByDate.get(e.date) || 0,
        thoughtScore: average(scores)
      });
    }
  });
  
  if (matchedDays.length < 7) return null;
  
  // Split by meditation practice
  const withMeditation = matchedDays.filter(d => d.meditationMinutes > 0);
  const withoutMeditation = matchedDays.filter(d => d.meditationMinutes === 0);
  
  if (withMeditation.length < 3 || withoutMeditation.length < 3) return null;
  
  const avgScoreWithMeditation = average(withMeditation.map(d => d.thoughtScore));
  const avgScoreWithoutMeditation = average(withoutMeditation.map(d => d.thoughtScore));
  const diff = avgScoreWithoutMeditation - avgScoreWithMeditation;
  
  let interpretation = '';
  if (diff > 1) {
    interpretation = `On days when you meditate, you tend to experience less rumination and fewer intrusive thoughts (avg ${avgScoreWithMeditation.toFixed(1)} vs ${avgScoreWithoutMeditation.toFixed(1)}). Meditation appears to support mental quieting for you.`;
  } else if (diff > 0.3) {
    interpretation = `There's a gentle link between meditation practice and fewer repetitive thought patterns. Consistent practice may deepen this effect.`;
  } else {
    interpretation = `Meditation doesn't show a clear pattern with thought intrusions yet. Different meditation styles may work better for you.`;
  }
  
  return {
    correlation_type: 'meditation_rumination_intrusions',
    metric_a: 'Meditation Practice',
    metric_b: 'Rumination & Intrusive Thoughts',
    avg_metric_a_low: 0,
    avg_metric_a_high: average(withMeditation.map(d => d.meditationMinutes)),
    avg_metric_b_low: avgScoreWithoutMeditation,
    avg_metric_b_high: avgScoreWithMeditation,
    interpretation,
    sample_size: matchedDays.length
  };
}

/**
 * Correlate ultra-processed food with low mood and food noise
 */
export async function getCorrelationUPFVsLowMoodAndFoodNoise(
  userId: string,
  days: number = 60
): Promise<EmotionalCorrelationResult | null> {
  const startDate = format(subDays(new Date(), days), 'yyyy-MM-dd');
  
  // Fetch emotional logs with low mood and food noise
  const { data: emotionalLogs, error: emotionalError } = await supabase
    .from('emotional_symptom_logs')
    .select('date, low_mood_score, food_noise_score')
    .eq('user_id', userId)
    .gte('date', startDate);
    
  if (emotionalError || !emotionalLogs?.length) return null;
  
  // Fetch food logs - count ultra-processed items per day
  const { data: foodLogs, error: foodError } = await supabase
    .from('food_logs')
    .select('date, processing_level')
    .eq('user_id', userId)
    .gte('date', startDate);
    
  if (foodError) return null;
  
  // Count UPF items by date
  const upfByDate = new Map<string, number>();
  foodLogs?.forEach(f => {
    if (f.processing_level === 'ultra_processed') {
      upfByDate.set(f.date, (upfByDate.get(f.date) || 0) + 1);
    }
  });
  
  const matchedDays: { upfCount: number; combinedScore: number }[] = [];
  emotionalLogs.forEach(e => {
    const lowMood = e.low_mood_score !== null ? Number(e.low_mood_score) : null;
    const foodNoise = e.food_noise_score !== null ? Number(e.food_noise_score) : null;
    
    const scores = [lowMood, foodNoise].filter((s): s is number => s !== null);
    if (scores.length > 0) {
      matchedDays.push({
        upfCount: upfByDate.get(e.date) || 0,
        combinedScore: average(scores)
      });
    }
  });
  
  if (matchedDays.length < 7) return null;
  
  // Split by UPF consumption
  const highUPF = matchedDays.filter(d => d.upfCount >= 2);
  const lowUPF = matchedDays.filter(d => d.upfCount < 2);
  
  if (highUPF.length < 3 || lowUPF.length < 3) return null;
  
  const avgScoreHighUPF = average(highUPF.map(d => d.combinedScore));
  const avgScoreLowUPF = average(lowUPF.map(d => d.combinedScore));
  const diff = avgScoreHighUPF - avgScoreLowUPF;
  
  let interpretation = '';
  if (diff > 1) {
    interpretation = `On days with more ultra-processed foods, you tend to report higher low mood and food noise scores (avg ${avgScoreHighUPF.toFixed(1)} vs ${avgScoreLowUPF.toFixed(1)}). Whole foods may support both your mood and relationship with eating.`;
  } else if (diff > 0.3) {
    interpretation = `There's a gentle pattern linking processed food intake to slightly elevated mood and food preoccupation scores. Your system may be sensitive to food quality.`;
  } else {
    interpretation = `Food processing level doesn't show a strong connection to low mood or food noise in this period.`;
  }
  
  return {
    correlation_type: 'upf_lowmood_foodnoise',
    metric_a: 'Ultra-Processed Foods',
    metric_b: 'Low Mood & Food Noise',
    avg_metric_a_low: average(lowUPF.map(d => d.upfCount)),
    avg_metric_a_high: average(highUPF.map(d => d.upfCount)),
    avg_metric_b_low: avgScoreLowUPF,
    avg_metric_b_high: avgScoreHighUPF,
    interpretation,
    sample_size: matchedDays.length
  };
}

/**
 * Correlate movement/exercise with self-sabotage and mental fatigue
 */
export async function getCorrelationMovementVsSelfSabotageAndMentalFatigue(
  userId: string,
  days: number = 60
): Promise<EmotionalCorrelationResult | null> {
  const startDate = format(subDays(new Date(), days), 'yyyy-MM-dd');
  
  // Fetch emotional logs with self-sabotage and mental fatigue
  const { data: emotionalLogs, error: emotionalError } = await supabase
    .from('emotional_symptom_logs')
    .select('date, self_sabotage_score, mental_fatigue_score')
    .eq('user_id', userId)
    .gte('date', startDate);
    
  if (emotionalError || !emotionalLogs?.length) return null;
  
  // Fetch movement/exercise activities
  const { data: activities, error: activityError } = await supabase
    .from('wellness_activities')
    .select('started_at, duration_minutes, activity_type')
    .eq('user_id', userId)
    .gte('started_at', `${startDate}T00:00:00`);
    
  if (activityError) return null;
  
  // Filter for movement-related activities and aggregate by date
  const movementTypes = ['exercise', 'walk', 'yoga', 'run', 'workout', 'swim', 'cycle', 'dance', 'stretch', 'pilates'];
  const movementByDate = new Map<string, number>();
  activities?.forEach(a => {
    const activityLower = (a.activity_type || '').toLowerCase();
    const isMovement = movementTypes.some(type => activityLower.includes(type));
    if (isMovement) {
      const date = a.started_at.split('T')[0];
      movementByDate.set(date, (movementByDate.get(date) || 0) + (a.duration_minutes || 0));
    }
  });
  
  const matchedDays: { movementMinutes: number; combinedScore: number }[] = [];
  emotionalLogs.forEach(e => {
    const selfSabotage = e.self_sabotage_score !== null ? Number(e.self_sabotage_score) : null;
    const mentalFatigue = e.mental_fatigue_score !== null ? Number(e.mental_fatigue_score) : null;
    
    const scores = [selfSabotage, mentalFatigue].filter((s): s is number => s !== null);
    if (scores.length > 0) {
      matchedDays.push({
        movementMinutes: movementByDate.get(e.date) || 0,
        combinedScore: average(scores)
      });
    }
  });
  
  if (matchedDays.length < 7) return null;
  
  // Split by median movement
  const movementMedian = median(matchedDays.map(d => d.movementMinutes));
  const lowMovement = matchedDays.filter(d => d.movementMinutes < movementMedian);
  const highMovement = matchedDays.filter(d => d.movementMinutes >= movementMedian);
  
  if (lowMovement.length < 3 || highMovement.length < 3) return null;
  
  const avgScoreLowMovement = average(lowMovement.map(d => d.combinedScore));
  const avgScoreHighMovement = average(highMovement.map(d => d.combinedScore));
  const diff = avgScoreLowMovement - avgScoreHighMovement;
  
  let interpretation = '';
  if (diff > 1) {
    interpretation = `On days with more movement, you tend to report less self-sabotage and mental fatigue (avg ${avgScoreHighMovement.toFixed(1)} vs ${avgScoreLowMovement.toFixed(1)}). Physical activity appears to support your mental resilience.`;
  } else if (diff > 0.3) {
    interpretation = `There's a gentle pattern linking more movement to slightly lower self-sabotage and mental fatigue scores. Consistent movement may strengthen this effect.`;
  } else {
    interpretation = `Movement doesn't show a strong pattern with self-sabotage or mental fatigue yet. Different types or intensities of activity may work differently for you.`;
  }
  
  return {
    correlation_type: 'movement_selfsabotage_fatigue',
    metric_a: 'Movement Minutes',
    metric_b: 'Self-Sabotage & Mental Fatigue',
    avg_metric_a_low: average(lowMovement.map(d => d.movementMinutes)),
    avg_metric_a_high: average(highMovement.map(d => d.movementMinutes)),
    avg_metric_b_low: avgScoreLowMovement,
    avg_metric_b_high: avgScoreHighMovement,
    interpretation,
    sample_size: matchedDays.length
  };
}

/**
 * Get all emotional correlations for a user
 */
export async function getAllEmotionalCorrelations(
  userId: string,
  days: number = 60
): Promise<EmotionalCorrelationResult[]> {
  const results = await Promise.all([
    // Original correlations
    getSleepAnxietyCorrelation(userId, days),
    getEFTPanicCorrelation(userId, days),
    getUPFMoodCorrelation(userId, days),
    getMeditationTraumaCorrelation(userId, days),
    getRuminationMoodCorrelation(userId, days),
    // Extended emotional/mental correlations
    getCorrelationSleepVsBrainFog(userId, days),
    getCorrelationEFTVsAnxietyAndPanic(userId, days),
    getCorrelationMeditationVsRuminationAndIntrusions(userId, days),
    getCorrelationUPFVsLowMoodAndFoodNoise(userId, days),
    getCorrelationMovementVsSelfSabotageAndMentalFatigue(userId, days)
  ]);
  
  return results.filter((r): r is EmotionalCorrelationResult => r !== null);
}
